
import React, { useCallback, useEffect, useState } from 'react';
import { Box, styled, Typography } from '@mui/material';
import GenericDraggableTableView from '../../../../Features/Table/GenericDraggableTable';
import CartLoader from '../../../../Helper/Loaders/CartLoader';
import { useTranslation } from 'react-i18next';
import { getColumnsConfig } from '../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations';
import { usePagesContent } from '../../../../Hooks/usePagesContent';
import { IDDisplayBox } from '../../../../Theme/DigitalTwinStyles';
import { getRealTimeHealthV2 } from '../../../../Api/SpecificBatteryApi';
import { useVehicleInfo } from '../../../../Pages/DigitalTwinV3/DigitalTwinContext';
import ProgressBarColumnView from '../../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/ProgressBarColumnView';
import { formatComputedDate } from '../../../../Helper/DatePicker/DateConverter';
import { getSOHColor } from '../../../../Helper/BatteryHealth/BatteryHealthHelper';

const TableContainer = styled(Box)(() => ({
    marginTop: '20px',
    width: '96.5%',
    minHeight: '200px',
    '& .ant-table-cell-with-append': {
        display: 'flex',
        alignItems: 'center'
    }
}));

// Resolves the SOH bar's props from the row + column meta. Values can arrive as
// numeric strings, so parse before formatting. Decimal places, empty-cell text,
// bar start and unit all come from the column config (org meta) with the app's
// usual defaults.
export const getSohBarProps = (record, config) => {
    const soh = parseFloat(record?.[config?.dataIndex]);
    const decimalPlaces = config?.decimalPlaces ?? 2;
    return {
        value: isNaN(soh) ? null : soh.toFixed(decimalPlaces),
        barColor: getSOHColor(soh),
        defaultText: config?.defaultValue || 'Not Available',
        minProgress: config?.startProgressFrom || 0,
        unitPostFix: config?.unitPostfix ?? '%',
    };
};

const customRenderers = {
    // Same colour scale and formatting as every other SOH bar in the app
    // (Inventory list, Performance event table) — colour by SOH band, decimals
    // driven by the column's meta config.
    'soh-progress-bar': (record, config) => (
        <ProgressBarColumnView {...getSohBarProps(record, config)} record={record} config={config} />
    ),
    // Field-driven date cell (DD MMM YYYY) — e.g. sohComputedDate
    'date': (record, config) => (
        <Typography variant="fw400s12c494949">
            {formatComputedDate(record?.[config.dataIndex])}
        </Typography>
    ),
};

const getApiType = (visibleBatteryParts) => {
    const normalized = (visibleBatteryParts || []).map((v) => v.toLowerCase());
    return normalized.includes('string') ? 'string' : 'pack';
};

const isPositiveNumber = (value) => {
    const n = parseFloat(value);
    return !isNaN(n) && n > 0;
};

/**
 * Entity column label for a string / pack row of the real_time v2 response:
 * "String <stringNo>" or "Pack <absPackNo>", else the 1-based row position.
 * The response is marshalled with every field of both shapes, so a pack row arrives
 * with stringNo: 0 (and a string row with absPackNo: 0); 0 is never a real number.
 */
export const getEntityRowLabel = (type, item, index) => {
    const number = type === 'string' ? item?.stringNo : item?.absPackNo;
    const label = type === 'string' ? 'String' : 'Pack';
    return `${label} ${isPositiveNumber(number) ? number : index + 1}`;
};

const hasBatteryLevel = (visibleBatteryParts) =>
    (visibleBatteryParts || []).map((v) => v.toLowerCase()).includes('battery');

const BatteryLifecycleMetricsV2 = ({ batteryInfo, visibleBatteryParts, batteryID, onBatterySohComputedDate }) => {
    const { t } = useTranslation(['common', 'generic_table']);
    const pageContent = usePagesContent();
    const vehicleInfo = useVehicleInfo();
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const tableConfig = pageContent?.metaInfo?.batteryHealth?.tables?.LifecycleMetrics;
    const metaColumnsConfig = tableConfig?.columnsConfig || {};
    const metaColumnOrdering = tableConfig?.columnOrdering || [];

    const tableColumns = getColumnsConfig({
        tableColumns: metaColumnsConfig,
        tableColumnOrder: metaColumnOrdering,
        customRenderers,
        t,
    });

    const applySort = useCallback((key, order) => {
        setTableData((prev) => {
            const sorted = [...prev].sort((a, b) => {
                const aVal = a[key] ?? '';
                const bVal = b[key] ?? '';
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return order === 'asc' ? aVal - bVal : bVal - aVal;
                }
                return order === 'asc'
                    ? String(aVal).localeCompare(String(bVal))
                    : String(bVal).localeCompare(String(aVal));
            });
            return sorted;
        });
    }, []);

    const setupTableData = useCallback(async (signal) => {
        if (!batteryID) {
            setIsLoading(false);
            onBatterySohComputedDate?.(null);
            return;
        }
        const type = getApiType(visibleBatteryParts);
        const includeBattery = hasBatteryLevel(visibleBatteryParts);
        setIsLoading(true);
        try {
            const fetches = [getRealTimeHealthV2({ batteryID, type })];
            if (includeBattery) fetches.push(getRealTimeHealthV2({ batteryID, type: 'battery' }));

            const [subRes, batteryRes] = await Promise.all(fetches);
            if (signal?.aborted) return;

            const normalizeRow = (item, i, keyPrefix = '') => ({
                ...item,
                // sohConfidence is now a literal string ("low" | "medium" | "high"), not numeric
                sohConfidence: item.sohConfidence ?? null,
                // Per-entity last-computed date; rendered as "DD MMM YYYY" via the "date" column
                sohComputedDate: item.sohComputedDate ?? null,
                key: item.stringConfigID || item.packConfigID || `${keyPrefix}row-${i}`,
            });

            // Entity label: "String 1"/"String 2"… for string topology, "Pack 1"… for pack topology
            const subRows = (subRes?.response?.data || []).map((item, i) => ({
                ...normalizeRow(item, i),
                stringNo: getEntityRowLabel(type, item, i),
            }));

            const batteryRows = includeBattery
                ? (batteryRes?.response?.data || []).map((item, i) => ({
                      ...normalizeRow(item, i, 'battery-'),
                      stringNo: 'Battery System',
                  }))
                : [];

            setTableData([...batteryRows, ...subRows]);
            // Lift the battery-level computed date to the parent for the chart
            // caption, so the tab does not fire a second identical real_time call.
            onBatterySohComputedDate?.(batteryRows[0]?.sohComputedDate ?? null);
        } catch (err) {
            if (!signal?.aborted) console.error('BatteryLifecycleMetrics fetch error:', err);
        } finally {
            if (!signal?.aborted) setIsLoading(false);
        }
    }, [batteryID, visibleBatteryParts, onBatterySohComputedDate]);

    useEffect(() => {
        const controller = new AbortController();
        setupTableData(controller.signal);
        return () => controller.abort();
    }, [setupTableData]);

    if (isLoading) {
        return <CartLoader />;
    }

    const headerIdentifier = tableConfig?.headerIdentifier;
    const identifierLabel = headerIdentifier?.label || t('common:device_id');
    const identifierField = headerIdentifier?.field || 'deviceID';
    // Look up: batteryInfo → vehicleInfo → first loaded row
    const identifierValue =
        batteryInfo?.[identifierField] ??
        vehicleInfo?.[identifierField] ??
        tableData?.[0]?.[identifierField];

    const customRightActionsView = identifierValue ? (
        <IDDisplayBox>
            <Box>
                <Typography variant="fw400s12c494949">{identifierLabel}: </Typography>
                <Typography variant="fw400s12c494949">{identifierValue}</Typography>
            </Box>
        </IDDisplayBox>
    ) : null;

    return (
        <TableContainer>
            <GenericDraggableTableView
                tableID={'LifecycleMetrics'}
                title={'Lifecycle Metrics'}
                containerStyles={{ width: '100%' }}
                disableSearch={true}
                disableDownloads={true}
                disableFiltersSettings={true}
                disableColumnSettings={true}
                showPrimaryButton={false}
                tableColumns={tableColumns}
                tableData={tableData}
                isLoading={isLoading}
                applySort={applySort}
                disablePagination={true}
                emptyMessage="No data found"
                customRightActions={!!customRightActionsView}
                customRightActionsView={customRightActionsView}
            />
        </TableContainer>
    );
};

export default BatteryLifecycleMetricsV2;
